import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  customers as seedCustomers,
  devices as seedDevices,
  alerts as seedAlerts,
  tickets as seedTickets,
  technicians as seedTechnicians,
  plans as seedPlans,
} from '../data/mockData';

const AppContext = createContext(null);

const DEMO_EMAIL = 'admin@jbnotify.com';
const DEMO_PASSWORD = 'demo123';

export function AppProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem('jbnotify_user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [company, setCompany] = useState('JB Heating & Air');
  const [devices, setDevices] = useState(seedDevices);
  const [alerts, setAlerts] = useState(seedAlerts);
  const [tickets, setTickets] = useState(seedTickets);
  const [customers] = useState(seedCustomers);
  const [technicians] = useState(seedTechnicians);
  const [plans] = useState(seedPlans);

  useEffect(() => {
    if (user) localStorage.setItem('jbnotify_user', JSON.stringify(user));
    else localStorage.removeItem('jbnotify_user');
  }, [user]);

  const login = (email, password) => {
    if (
      email.trim().toLowerCase() === DEMO_EMAIL &&
      password === DEMO_PASSWORD
    ) {
      const u = {
        email: DEMO_EMAIL,
        name: 'Jordan Bell',
        role: 'Service Manager',
        initials: 'JB',
      };
      setUser(u);
      return { ok: true };
    }
    return { ok: false, error: 'Invalid credentials. Try the demo login.' };
  };

  const logout = () => setUser(null);

  const updateTicketStatus = (id, status) => {
    setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
  };

  const addTicketNote = (id, text) => {
    if (!text || !text.trim()) return;
    const note = {
      author: user?.name || 'You',
      text: text.trim(),
      ts: 'just now',
    };
    setTickets((prev) =>
      prev.map((t) => (t.id === id ? { ...t, notes: [...t.notes, note] } : t))
    );
  };

  const updateAlertStatus = (id, status) => {
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
  };

  const createTicketFromAlert = (alert) => {
    const newTicket = {
      id: `TKT-${String(Date.now()).slice(-6)}`,
      customerId: alert.customerId,
      customerName: alert.customer,
      address: alert.address,
      deviceId: alert.deviceId,
      issue: alert.type,
      alertSource: alert.id,
      priority: alert.severity === 'critical' ? 'High' : alert.severity === 'warning' ? 'Medium' : 'Low',
      technician: alert.assignedTechnician || 'Unassigned',
      scheduledAt: 'To be scheduled',
      estRevenue: 285,
      status: 'new',
      createdAt: new Date().toISOString(),
      notes: [],
      partsNeeded: [],
      checklist: [
        'Verify reported symptoms against diagnostic data',
        'Inspect filter and airflow path',
        'Check refrigerant levels and pressures',
        'Validate thermostat wiring and sensor calibration',
      ],
    };
    setTickets((prev) => [newTicket, ...prev]);
    updateAlertStatus(alert.id, 'scheduled');
    return newTicket;
  };

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      company,
      setCompany,
      devices,
      setDevices,
      alerts,
      setAlerts,
      tickets,
      setTickets,
      customers,
      technicians,
      plans,
      updateTicketStatus,
      addTicketNote,
      updateAlertStatus,
      createTicketFromAlert,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, devices, alerts, tickets, company]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
